import {
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { auth } from "../../../utils/FireBaseConfigurations/config";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { fetchWithTimeout } from "../../../utils/fetchWithTimeout";
import FormImage from "../../../assests/FormImage.png";
import confetti from "canvas-confetti";
import { theme } from "../../../theme"; 
import ErrorIcon from "@mui/icons-material/Error";
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
 
// Business email validation function
const isValidBusinessEmail = (email) => {
  // First check basic email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return false;
  }
 
  // List of personal/free email domains to exclude
  const personalDomains = [
    "gmail.com",
    "gmail.co",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "live.com",
    "msn.com",
    "ymail.com",
    "protonmail.com",
    "mail.com",
    "zoho.com",
    "rediffmail.com",
  ];
 
  // Extract domain from email
  const domain = email.toLowerCase().split("@")[1]; // Reject any domain that contains "gmail" anywhere (e.g., gmail.co, sub.gmail.net)
  if (domain.includes("gmail")) {
    return false;
  }
  // Return false if it's a personal domain
  return !personalDomains.includes(domain);
};
 
// Name validation function (only alphabets and spaces)
const isValidName = (name) => {
  const nameRegex = /^[A-Za-z\s]+$/;
  return nameRegex.test(name) && name.trim().length > 0;
};
 
// Mobile number validation function (only numbers)
const isValidMobileNumber = (phone) => {
  // Remove country code and separators and validate length
  const numbersOnly = String(phone || "").replace(/\D/g, "");
  // Backend expects 10-20 characters (schemas/lead.py)
  return numbersOnly.length >= 10 && numbersOnly.length <= 20;
};

// Common function to format phone number with E164 format with space
const formatE164WithSpace = (phone, dialCode) => {
  if (!phone) return "";
  const digits = String(phone || "").replace(/\D/g, "");

  if (dialCode) {
    const dc = String(dialCode).replace(/\D/g, "");
    if (digits.startsWith(dc) && digits.length > dc.length) {
      return `+${dc} ${digits.slice(dc.length)}`;
    }
  }

  const match = digits.match(/^(\d{1,3})(\d{6,})$/);
  if (match) {
    return `+${match[1]} ${match[2]}`;
  }

  if (digits.length >= 7) {
    const ccMatch = digits.match(/^(\d{1,3})(.+)$/);
    if (ccMatch) {
      return `+${ccMatch[1]} ${ccMatch[2]}`;
    }
  }

  return phone.startsWith("+") ? phone : `+${phone}`;
};

// Simple contact form fields
const simpleContactFields = [
  {
    label: "Full Name",
    type: "text",
    name: "fullName",
    required: true,
    validation: "name"
  },
  {
    label: "Mobile Number",
    type: "phone",
    name: "phone",
    required: true,
    validation: "mobile"
  },
  {
    label: "Business Email",
    type: "email",
    name: "businessEmail",
    required: true,
    validation: "businessEmail"
  },
  {
    label: "Message",
    type: "textarea",
    name: "message",
    required: false
  },
];

const fields = [
  { label: "Full Name", type: "text", name: "fullName", validation: "name" },
  { label: "Mobile No.", type: "phone", name: "phone", validation: "mobile" },
  { label: "Company Name", type: "text", name: "companyName" },
  { label: "Business Email", type: "email", name: "businessEmail" },
  {
    label: "Role",
    type: "select",
    options: [
      "Owner / Founder",
      "CEO / Director",
      "Project Manager",
      "Product Manager",
      "Finance / Accountant",
      "Developer / Engineer",
      "System Administrator",
      "Other",
    ],
    name: "role",
  },
  { label: "Reward", type: "readonly", name: "reward" },
  { label: "Message", type: "textarea", name: "message" },
];
 
const CreditsRequestsForm = ({ onClose, onCreditsRefetch, onStatusChange, onSignupTrigger }) => {
  const [inputValues, setInputValues] = useState({
    fullName: "",
    phone: "", // Changed from countryCode and mobileNo to single phone field
    businessEmail: "",
    companyName: "",
    role: "", // It's better to initialize Autocomplete value as null
    numberOfCreditsRequired: "100",
    message: "",
  });
  // Track dial code from PhoneInput for accurate country-aware formatting
  const [phoneDialCode, setPhoneDialCode] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationCheckInterval, setVerificationCheckInterval] =
    useState(null);
  const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(false);
  const [isCheckingSubmission, setIsCheckingSubmission] = useState(true);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [lastSubmittedContactData, setLastSubmittedContactData] = useState(null);
  const [isContactFormSubmitted, setIsContactFormSubmitted] = useState(false);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  // Check if user has already submitted the lead form
  useEffect(() => {
    const checkIfAlreadySubmitted = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.uid) {
          console.log("⚠️ No user found, skipping check");
          setIsUserAuthenticated(false);		
          setIsCheckingSubmission(false);
          return;
        }
	setIsUserAuthenticated(true);      
        const checkUrl = `${BACKEND_URL}/lead/checkLeadForm/${currentUser.uid}`;
        const response = await fetchWithTimeout(
          checkUrl,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
          10000
        );
        if (response.ok) {
          const data = await response.json();
          setHasAlreadySubmitted(data.has_submitted || false);
        } else {
          const errorText = await response.text().catch(() => "");
        }
      } catch (err) {
        console.error("❌ Error checking lead form submission:", err);
      } finally {
        setIsCheckingSubmission(false);
      }
    };
 
    checkIfAlreadySubmitted();
  }, []);
 
  // Notify parent when submission status changes
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({ isAlreadySubmitted: hasAlreadySubmitted });
    }
  }, [hasAlreadySubmitted, onStatusChange]);

  // Check verification status when email changes
  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (
        inputValues.businessEmail &&
        isValidBusinessEmail(inputValues.businessEmail)
      ) {
        const isVerified = await checkEmailVerification(
          inputValues.businessEmail
        );
       if (isVerified) {
          // Check if email already exists in database
          const emailExists = await checkEmailExists(inputValues.businessEmail);
          setEmailAlreadyExists(emailExists);
          setIsEmailVerified(!emailExists);
        } else {
          setIsEmailVerified(false);
          setEmailAlreadyExists(false);
        }
      } else {
        setIsEmailVerified(false);
        setEmailAlreadyExists(false);	      
      }
    };
 
    // Debounce the check
    const timeoutId = setTimeout(checkVerificationStatus, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValues.businessEmail]);
 
  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (verificationCheckInterval) {
        clearInterval(verificationCheckInterval);
      }
    };
  }, [verificationCheckInterval]);
 
  // Ensure message styles are available once in the document
  const ensureConfettiStyles = () => {
    if (document.getElementById("confetti-message-styles")) return;
    const style = document.createElement("style");
    style.id = "confetti-message-styles";
    style.innerHTML = `
      .confetti-message{position:fixed;top:30%;left:50%;transform:translate(-50%,-50%);background:transparent;color:#2196f3;font-weight:bold;padding:20px 40px;border-radius:12px;font-size:1.6rem;text-align:center;z-index:9999;opacity:0;animation:confettiFadeIn .5s forwards}
      @keyframes confettiFadeIn{from{opacity:0;transform:translate(-50%,-60%)}to{opacity:1;transform:translate(-50%,-50%)}}
    `;
    document.head.appendChild(style);
  };

  const launchCelebration = () => {
    console.log("🎉 Launching celebration...");
    ensureConfettiStyles();
    
    // Confetti burst - multiple bursts for better effect
    try {
      confetti({
        particleCount: 500,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#2196f3", "#4caf50", "#ff9800", "#e91e63", "#9c27b0"],
      });
      
      // Second burst after short delay
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#2196f3", "#4caf50", "#ff9800"],
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#2196f3", "#4caf50", "#ff9800"],
        });
      }, 500);
    } catch (error) {
      console.error("Confetti error:", error);
    }
    
    // Message element with better styling
    const msg = document.createElement("div");
    msg.className = "confetti-message";
    msg.textContent = "🎉 Congratulations! You've won 100 credits!";
    msg.style.position = "fixed";
    msg.style.top = "30%";
    msg.style.left = "50%";
    msg.style.transform = "translate(-50%, -50%)";
    msg.style.background = "rgba(255, 255, 255, 0.95)";
    // msg.style.background = "linear-gradient(90deg, #ff6b6b, #feca57, #1dd1a1, #54a0ff, #5f27cd)";
    msg.style.color = "#2196f3";
    msg.style.fontWeight = "bold";
    msg.style.padding = "20px 40px";
    msg.style.borderRadius = "12px";
    msg.style.fontSize = "1.6rem";
    msg.style.textAlign = "center";
    msg.style.zIndex = "99999";
    msg.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.3)";
    msg.style.opacity = "0";
    msg.style.animation = "confettiFadeIn 0.5s forwards";
    
    document.body.appendChild(msg);
    
    setTimeout(() => {
      msg.style.opacity = "0";
      msg.style.transition = "opacity 0.3s";
      setTimeout(() => {
        msg.remove();
        // Reload the page after celebration completes to update credit count
        window.location.reload();
      }, 300);
    }, 3000);
    
    console.log("✅ Celebration launched!");
  };

  // Field validation function
  const validateField = (name, value, isSimpleForm = false) => {
let errorMessage = "";

    switch (name) {
      case "fullName":
        if (!value.trim()) {
          errorMessage = "Full name is required";
        } else if (!isValidName(value)) {
          errorMessage = "Full name should contain only alphabets";
        }
        break;
      case "phone":
        if (!value.trim()) {
          errorMessage = "Phone number is required";
        } else if (!isValidMobileNumber(value)) {
          errorMessage = "Please enter a valid phone number";
        }
        break;
      case "businessEmail":
        if (!value.trim()) {
	  errorMessage = isSimpleForm ? "Email is required" : "Business email is required";
        } else if (!isValidBusinessEmail(value)) {
          errorMessage = "Please enter a valid business email address";
        }
        break;
      case "companyName":
        if (!value.trim()) {
          errorMessage = "Company name is required";
        }else if(!/[A-Za-z]/.test(value)){
          errorMessage = "Company name must include at least one letter.";
        }
        break;
      case "numberOfCreditsRequired":
	errorMessage = "";
        break;
      default:
        break;
    }

    return errorMessage;
  };
 
  // Handle input change; validate only if field has been touched
  const handleInputChange = (name, value, extra) => {
    if (name === "phone" && extra) {
      setPhoneDialCode(String(extra));
    }
    setInputValues({ ...inputValues, [name]: value });
    if (name === "businessEmail") {
      setIsEmailVerified(false);
      setEmailAlreadyExists(false);
      setShowVerificationMessage(false);
    } 
    // Validate on change only if already touched
    if (touchedFields[name]) {
      const errorMessage = validateField(name, value);
      setFieldErrors((prev) => ({
        ...prev,
        [name]: errorMessage,
      }));
    }
 
    // Clear general error if any field is being corrected
    if (error) setError("");
  };

  // Mark field as touched and validate on blur
  const handleBlur = (name) => {
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    const errorMessage = validateField(name, inputValues[name] || "");
    setFieldErrors((prev) => ({
      ...prev,
      [name]: errorMessage,
    }));
  };
 
  const isFormValid =
    inputValues.fullName &&
    inputValues.phone &&
    inputValues.businessEmail &&
    inputValues.companyName &&
    inputValues.role &&
    isEmailVerified &&
    !emailAlreadyExists &&
    isValidName(inputValues.fullName) &&
    isValidMobileNumber(inputValues.phone) &&
    isValidBusinessEmail(inputValues.businessEmail) &&
    !Object.values(fieldErrors).some((error) => error);
 
  const checkEmailVerification = async (email) => {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/email-verification/check/${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
        10000
      );
 
      if (response.ok) {
        const data = await response.json();
        return data.is_verified;
      }
      return false;
    } catch (err) {
      console.error("Error checking email verification:", err);
      return false;
    }
  };
 const checkEmailExists = async (email) => {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/lead/checkEmailExists/${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
        10000
      );
 
      if (response.ok) {
        const data = await response.json();
        return data.exists;
      }
      return false;
    } catch (err) {
      console.error("Error checking email existence:", err);
      return false;
    }
  };
  const sendVerificationEmail = async (email) => {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/email-verification/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
        30000
      );
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || "Failed to send verification email"
        );
      }
 
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };
 
  const handleVerifyEmail = async () => {
    if (
      !inputValues.businessEmail ||
      !isValidBusinessEmail(inputValues.businessEmail)
    ) {
      setError("Please enter a valid business email address");
      return;
    }
 
    setIsVerifying(true);
    setError("");
    setShowVerificationMessage(false);
    setEmailAlreadyExists(false);
    try {
      // First check if already verified
      const isVerified = await checkEmailVerification(
        inputValues.businessEmail
      );
 
      if (isVerified) {
	const emailExists = await checkEmailExists(inputValues.businessEmail);
        if (emailExists) {
          setEmailAlreadyExists(true);
          setIsEmailVerified(false);
          return;
        }      
        setIsEmailVerified(true);
        setShowVerificationMessage(false);
	setEmailAlreadyExists(false);
        return;
      }
 
      // Send verification email
      const verificationData = await sendVerificationEmail(
        inputValues.businessEmail
      );
 
      if (verificationData.already_verified) {
	const emailExists = await checkEmailExists(inputValues.businessEmail);
        if (emailExists) {
          setEmailAlreadyExists(true);
          setIsEmailVerified(false);
          return;
        }
        setIsEmailVerified(true);
        setEmailAlreadyExists(false);
	setShowVerificationMessage(false);
      } else {
        setShowVerificationMessage(true);
        // setError("Verification email sent! Please check your inbox and click the verification link.");
 
        // Start polling for verification status every 5 seconds
        const intervalId = setInterval(async () => {
          const verified = await checkEmailVerification(
            inputValues.businessEmail
          );
          if (verified) {
            // Check if email already exists in database
            const emailExists = await checkEmailExists(
              inputValues.businessEmail
            );
            if (emailExists) {
              setEmailAlreadyExists(true);
              setIsEmailVerified(false);
              setShowVerificationMessage(false);
              clearInterval(intervalId);
              return;
            }
            setIsEmailVerified(true);
            setShowVerificationMessage(false);
            setEmailAlreadyExists(false);
            setError("");
            clearInterval(intervalId);
          }
        }, 5000);
 
        setVerificationCheckInterval(intervalId);
 
        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(intervalId);
        }, 300000);
      }
    } catch (err) {
      console.error("Email verification error:", err);
      setError(
        err.message || "Failed to send verification email. Please try again."
      );
    } finally {
      setIsVerifying(false);
    }
  };

// Simple contact form submit for non-authenticated users
  const handleSimpleContactSubmit = async () => {
    // Validate simple form fields using business email validation
    const fullNameError = validateField("fullName", inputValues.fullName, true);
    const phoneError = validateField("phone", inputValues.phone, true);
    const emailError = validateField("businessEmail", inputValues.businessEmail, true);
 
    if (fullNameError) {
      setError(fullNameError);
      return;
    }
    if (phoneError) {
      setError(phoneError);
      return;
    }
    if (emailError) {
      setError(emailError);
      return;
    }
 
    setIsSubmitting(true);
    setError("");
 
    try {
      const contactPayload = {
        fullName: inputValues.fullName,
        mobileNumber: formatE164WithSpace(inputValues.phone, phoneDialCode),
        email: inputValues.businessEmail,
        message: inputValues.message || "",
      };
 
      console.log("📤 Simple Contact Form Payload:", contactPayload);
 
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/contact/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactPayload),
        },
        30000
      );
 
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        let message = "Failed to submit contact form";
        try {
          const json = JSON.parse(errorText);
          message = json?.detail || message;
        } catch (_) {
          if (errorText) message = errorText;
        }
        throw new Error(message);
      }
 
      const data = await response.json();
      console.log("✅ Contact form submitted successfully:", data);
 
      // Show success message in dialog
      setError("");
      const mobileNumber = formatE164WithSpace(inputValues.phone, phoneDialCode);
      setLastSubmittedContactData({
        businessEmail: inputValues.businessEmail,
        fullName: inputValues.fullName,
        mobileNumber,
        message: inputValues.message || "",
        companyName: inputValues.companyName || "",
        role: inputValues.role || "",
      });
      setShowSuccessMessage(true);
     
      // Clear all form fields
      setInputValues({
        fullName: "",
        phone: "",
        businessEmail: "",
        companyName: "",
        role: "",
        numberOfCreditsRequired: "100",
        message: "",
      });
      setPhoneDialCode("");
      setFieldErrors({});
      setTouchedFields({});
     
      // Disable submit button until page reload
      setIsContactFormSubmitted(true);
    } catch (err) {
      console.error("❌ Contact form submission error:", err);
      setError(err.message || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateAnonId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `anon-${crypto.randomUUID()}`;
    }
    return `anon-${Date.now()}`;
  };
  const getFallbackEmail = () => {
    const anonId = generateAnonId();
    return `${anonId}@guest.trueid`;
  };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
  
    if (!isEmailVerified) {
      setError("Please verify your business email before submitting the form.");
      return;
    }
  
    setIsSubmitting(true);
    setError("");
    try {
	const currentUser = auth.currentUser;
    //	const firebaseUUID = currentUser.uid || `anon-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
	const firebaseUUID = currentUser?.uid ?? generateAnonId();
	const submissionEmail = currentUser?.email || getFallbackEmail();
/*
      // Returns "+<CC> <NSN>" using dial code from PhoneInput when available
function formatE164WithSpace(phone, dialCode) {
        if (!phone) return "";
        const digits = String(phone || "").replace(/\D/g, "");
        if(dialCode){
          const dc = String(dialCode).replace(/\D/g, "");
          if(digits.startsWith(dc) && digits.length > dc.length){
            return `+${dc} ${digits.slice(dc.length)}`;
          }
        }      
        const match = digits.match(/^(\d{1,3})(\d{6,})$/);
        if (match) {
          return `+${match[1]} ${match[2]}`;
        }
	// Fallback: if we have at least some digits, format with space
        if (digits.length >= 7) {
          // Assume first 1-3 digits are country code
          const ccMatch = digits.match(/^(\d{1,3})(.+)$/);
          if (ccMatch) {
            return `+${ccMatch[1]} ${ccMatch[2]}`;
          }
        }

        // Last resort: just add + if missing

        return phone.startsWith("+") ? phone : `+${phone}`;
      }*/
      // 👇 Construct the payload first
    const leadFormPayload = {
      UUID: firebaseUUID,
      FullName: inputValues.fullName,
      MobileNumber: formatE164WithSpace(inputValues.phone, phoneDialCode),
      BusinessEmail: inputValues.businessEmail,
      CompanyName: inputValues.companyName,
      Role: inputValues.role,
      NumberOfCreditsRequired: parseInt(inputValues.numberOfCreditsRequired, 10),
      Message: inputValues.message,
      current_loggedIn_mail: submissionEmail,
    };
  
      // 👇 Log payload before submitting
      console.log("📤 Lead Form Payload being submitted:", leadFormPayload);
  
      // API call
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/lead/submitLeadForm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(leadFormPayload),
        },
        30000
      );
  
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        let message = "Failed to submit lead form";
        try {
          const json = JSON.parse(errorText);
          message = json?.detail || message;
        } catch (_) {
          if (errorText) message = errorText;
        }
        throw new Error(message);
      }
  
      const data = await response.json();
      console.log("✅ Lead form submitted successfully:", data);
  
      // Celebration on successful submission
      launchCelebration();
      // Trigger credits refetch for parent UI
      if (typeof onCreditsRefetch === "function") {
        try {
          await onCreditsRefetch();
        } catch (e) {
          console.warn("Credits refetch failed:", e);
        }
      }
      // Close the dialog after showing congratulations
      if (onClose) onClose();
    } catch (err) {
      console.error("❌ Lead form submission error:", err);
      setError(err.message || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
   // Show loading state while checking
  if (isCheckingSubmission) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "600px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
	 // Simple contact form for non-authenticated users
  if (!isUserAuthenticated) {
  /*  // Show success message after form submission
    if (showSuccessMessage) {
      return (
        <Grid container sx={{ minHeight: "auto" }}>
          <Grid
            size={{ xs: 12 }}
            sx={{
              px: { xs: 2, md: 3 },
              pb: { xs: 3, md: 3 },
              pt: 3,
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              minHeight: "250px",
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 60,
                color: "success.main",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 1,
                color: "#333",
              }}
            >
              Thank You!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#666",
                maxWidth: "350px",
              }}
            >
              Thank you for contacting us! We'll get back to you soon.
            </Typography>
          </Grid>
        </Grid>
      );
    }
*/
    // Show contact form
    return (
      <Grid container sx={{ minHeight: "auto" }}>
        {/* Right Side - Simple Contact Form */}
        <Grid
          size={{ xs: 12 }}
          sx={{
            px: { xs: 2, md: 3 },
            pb: { xs: 2, md: 2 },
            pt: 2,
            backgroundColor: "#ffffff",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 2,
              textAlign: "center",
              color: "#333",
              fontSize: "1.1rem",
            }}
          >
            Contact Us
          </Typography>

          {/* Map through simple contact form fields */}
          {simpleContactFields.map((field) => (
            <Grid key={field.name} sx={{ mb: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.3, fontWeight: 500, fontSize: "0.8rem" }}
              >
                {field.label}
                {field.required && (
                  <span style={{ color: "#d32f2f" }}>&nbsp;*</span>
                )}
              </Typography>

              {field.type === "phone" ? (
                <>
                  <PhoneInput
                    country={"in"}
                    value={inputValues[field.name]}
                    onChange={(val, country) =>
                      handleInputChange(field.name, val, country?.dialCode || "")
                    }
                    onBlur={() => handleBlur(field.name)}
                    inputStyle={{
                      width: "100%",
                      height: "36px",
                      borderRadius: "6px",
                      border:
                        touchedFields[field.name] && fieldErrors[field.name]
                          ? "1px solid #d32f2f"
                          : "1px solid #ccc",
                      fontSize: "13px",
                    }}
                    containerStyle={{
                      width: "100%",
                      marginBottom: "0px",
                    }}
                    buttonStyle={{
                      height: "36px",
                      borderRadius: "6px 0 0 6px",
                    }}
                    dropdownStyle={{
                      maxHeight: "200px",
                    }}
                  />
                  {touchedFields[field.name] && fieldErrors[field.name] && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ mt: 0.3, fontSize: "0.7rem" }}
                    >
                      {fieldErrors[field.name]}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    type={field.type === "email" ? "email" : "text"}
                    value={inputValues[field.name]}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    onBlur={() => handleBlur(field.name)}
                    error={
                      !!(touchedFields[field.name] && fieldErrors[field.name])
                    }
                    multiline={field.type === "textarea"}
                    minRows={field.type === "textarea" ? 3 : undefined}
                    maxRows={field.type === "textarea" ? 3 : undefined}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        fontSize: "16px",
                        ...(field.type !== "textarea" && { height: "36px" }),
                        ...(field.type === "textarea" && {
                          minHeight: "80px",
                        }),
                      },
                    }}
                  />
                  {touchedFields[field.name] && fieldErrors[field.name] && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ mt: 0.25, fontSize: "0.7rem" }}
                    >
                      {fieldErrors[field.name]}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          ))}

          {error && (
            <Typography
              variant="caption"
              color="error"
              textAlign="center"
              display="block"
              sx={{ mt: 0.3, mb: 0.3, fontSize: "0.7rem" }}
            >
              {error}
            </Typography>
          )}

          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            size="small"
            disabled={
              isContactFormSubmitted ||		    
              isSubmitting ||
              !inputValues.fullName.trim() ||
              !inputValues.phone.trim() ||
              !inputValues.businessEmail.trim() ||
              !isValidName(inputValues.fullName) ||
              !isValidMobileNumber(inputValues.phone) ||
              !isValidBusinessEmail(inputValues.businessEmail)
            }
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              mt: 0.5,
              padding: "8px",
              borderRadius: "6px",
              fontSize: "0.875rem",
              "&:hover": { backgroundColor: "#333" },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#666",
              },
            }}
            onClick={handleSimpleContactSubmit}
          >
            {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
          </Button>

	  {showSuccessMessage && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon fontSize="inherit" />}
              sx={{
                mt: 1.5,
                borderRadius: "10px",
                fontSize: "0.85rem",
              }}
            >
              Thank You! Our sales team will contact you soon.
            </Alert>
          )}  

          {/* Signup Link */}
          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: "1px solid #e0e0e0",
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#666", mb: 0.8, fontSize: "0.8rem" }}
            >
              Want to check images for Deepfakes & AI-Generated content?
            </Typography>
            <Button
              variant="outlined"
              size="small"
            /*  onClick={() => {
                if (onSignupTrigger) {
                  onSignupTrigger();
                } else if (onClose) {
                  onClose();
                }
              }}*/
              sx={{
                textTransform: "none",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                fontSize: "0.8rem",
                padding: "5px 16px",
                borderRadius: "6px",
	        opacity: showSuccessMessage ? 1 : 0.5,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: "rgba(40, 171, 226, 0.04)",
                },
              }}
	    disabled={!showSuccessMessage}
              onClick={() => {
                if (!showSuccessMessage) return;
                if (onSignupTrigger) {
                  onSignupTrigger(lastSubmittedContactData);
                } else if (onClose) {
                  onClose();
                }
              }}
            >
              Sign Up Here
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  }

 // Show message if user has already submitted
  if (hasAlreadySubmitted) {
    return (
      <Grid
        size={{ xs: 12 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#ffffff",
          borderRadius: "20px",
          padding: 3,
          textAlign: "center",
          maxWidth: "500px",
          margin: "0 auto",
        }}
      >
        <CheckCircleIcon
          sx={{
            fontSize: 50,
            color: "success.main",
            mb: 1,
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#333",
          }}
          gutterBottom
        >
          Form Already Submitted
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#666",
          }}
        >
          Please contact us at sales@trueid.in for more credits
        </Typography>
      </Grid>
    );
  }
  return (
    <Grid 
      container 
      sx={{ height: "600px" }}
    >
      {/* Left Side - Image */}
      <Grid
        // item
        // xs={8}
        // md={2}
        size={{ xs: 0, md: 6 }}
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0a1447 0%, #1a237e 100%)",
          position: "relative",
          overflow: "auto",
          borderRadius: "20px 0 0 20px",
          padding: 0,
          minHeight: "600px",
          backgroundImage: `url(${FormImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* <Box
          component="img"
          src={FormImage}
          alt="TrueID"
          // sx={{
          //   width: "100%",
          //   maxHeight: "100%",
          //   height: "auto",
          //   objectFit: "contain",
          //   objectPosition: "center",
          //   border: "none",
          //   outline: "none",
          //   display: "block",
          // }}

        >
          
          </Box> */}
          {/* <img src={FormImage} width={"100%"} height={"100%"} alt="TrueID" /> */}
      </Grid>

      {/* Right Side - Form */}
      <Grid
        // item
        // xs={12}
        // md={10}
        size={{ xs: 12, md: 6 }}
        sx={{
          px: { xs: 2.5, md: 3 },
          pb: { xs: 2.5, md: 3 },
          pt: 4.8,
          backgroundColor: "#ffffff",
          borderRadius: { xs: "20px", md: "0 20px 20px 0" },
          display: "flex",
          flexDirection: "column",
        }}
      >
      {fields.map((item, index) => {
        return (
          <Grid key={index} sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.25, fontWeight: 500, fontSize: "0.875rem" }}>
              {item.label}
              {item.name !== "message" && item.name !== "reward" && (
                <span style={{ color: "#d32f2f" }}>&nbsp;*</span>
              )}
            </Typography>
            {item.type === "email" ? (
              <>
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="email"
                    value={inputValues[item.name]}
                    onChange={(e) =>
                      handleInputChange(item.name, e.target.value)
                    }
                    onBlur={() => handleBlur(item.name)}
                    error={!!(touchedFields[item.name] && fieldErrors[item.name])|| emailAlreadyExists}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "30px",
                      },
                      "& input:-webkit-autofill": {
                        WebkitBoxShadow: "0 0 0 1000px white inset",
                      },
                    }}
                  />
                    {emailAlreadyExists ? (
                      <ErrorIcon
                        sx={{
                          color: "error.main",
                          fontSize: 30,
                          flexShrink: 0,
                        }}
                      />
                    ) : isEmailVerified ? (
		    <CheckCircleIcon
                      sx={{
                        color: "success.main",
                        fontSize: 30,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleVerifyEmail}
                      disabled={
                        isVerifying ||
                        !inputValues.businessEmail ||
                        !isValidBusinessEmail(inputValues.businessEmail) ||
                        !!fieldErrors.businessEmail
                      }
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        minWidth: "80px",
                        height: "30px",
                        fontSize: "0.75rem",
                        padding: "4px 12px",
                        borderRadius: "8px",
                        flexShrink: 0,
                        "&:hover": { backgroundColor: "#333" },
                        "&:disabled": {
                          backgroundColor: "#ccc",
                          color: "#666",
                        },
                      }}
                    >
                      {isVerifying ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        "Verify"
                      )}
                    </Button>
                  )}
                </Box>
                {touchedFields[item.name] && fieldErrors[item.name] && (
                  <Typography
                    variant="caption"
                    color="error"
                    display="block"
                    sx={{ mt: 0.25, fontSize: "0.7rem" }}
                  >
                    {fieldErrors[item.name]}
                  </Typography>
                )}
                  {emailAlreadyExists && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ mt: 0.25, fontSize: "0.7rem" }}
                    >
                      This business email has already submitted this form.
                    </Typography>
                  )}
                  {showVerificationMessage && !emailAlreadyExists && (
		    <Typography
                    variant="body2"
                    fontSize="11px"
                    sx={{ mt: 0.25, color: "#00008B"  }}
                  >
                    Verification e-mail sent! Please check your inbox.
                  </Typography>
                )}
                    {isEmailVerified &&
                    inputValues.businessEmail &&
                    !emailAlreadyExists && (
		    <Typography
                      variant="body2"
		      color="success.main"
                      sx={{ ml: 0, mb: 0.5, fontSize: "11px"}}
                    >
                      Email verified successfully!
                    </Typography>
                  )}    
              </>
            ) : item.type === "text" || item.type === "number" || item.type === "textarea" ? (
              <>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  type={item.type === "textarea" ? undefined : item.type}
                  value={inputValues[item.name]}
                  onChange={(e) => handleInputChange(item.name, e.target.value)}
                  onBlur={() => handleBlur(item.name)}
                  error={!!(touchedFields[item.name] && fieldErrors[item.name])}
                  disabled={false}
                  multiline={item.type === "textarea"}
                  minRows={item.type === "textarea" ? 3 : undefined}
                  maxRows={item.type === "textarea" ? 3 : undefined}
                  inputProps={
                    item.type === "number"
                      ? {
                          min: 1,
                          step: 1,
                          onKeyPress: (e) => {
                            // Prevent non-numeric characters
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          },
                        }
                      : {}
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      ...(item.type !== "textarea" && { height: "30px" }),
                      ...(item.type === "textarea" && { 
                        display: "flex",
                        alignItems: "flex-start",
                        minHeight: "80px",
                        "& .MuiInputBase-input": {  
                          verticalAlign: "top",
                          padding: item.name === "message" ? "0px" : "6px 8px", // no padding for 'message'
                          margin: "0px",
                        },
                      }),
                    },
                    "& .MuiOutlinedInput-input": {
                      ...(item.type !== "textarea" && { padding: "6px 10px" }),
                      ...(item.type === "textarea" && { 
                        padding: item.name === "message" ? "0px" : "8px 14px", // reset padding for message
                      }),
                    },
                    "& .MuiInputBase-inputMultiline": {
                      padding: item.name === "message" ? "0px" : "0px 6px", // ensures clean edge start
                      textAlign: "left",
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                    },
                  }}
                  
                />
                {touchedFields[item.name] && fieldErrors[item.name] && (
                  <Typography
                    variant="caption"
                    color="error"
                    display="block"
                    sx={{ mt: 0.25, fontSize: "0.7rem" }}
                  >
                    {fieldErrors[item.name]}
                  </Typography>
                )}
                {item.name === "numberOfCreditsRequired" &&
                  !isEmailVerified && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{
                        mt: 0.25,
                        fontSize: "0.65rem",
                        fontStyle: "italic",
                      }}
                    >
                      You can enter number of credits after email verification
                    </Typography>
                  )}
              </>
            ) : item.type === "readonly" ? (
              <>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={"100 Credits"}
                  disabled
                  InputProps={{ readOnly: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      height: "30px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "6px 8px", // reduce left padding to remove extra whitespace
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                    },
                  }}
                />
              </>
            ) : item.type === "phone" ? (
              <>
                <PhoneInput
                  country={"in"} // Default country India
                  value={inputValues.phone}
                  onChange={(val, country) =>
                    handleInputChange("phone", val, country?.dialCode || "")
                  }
                  onBlur={() => handleBlur("phone")}
                  inputStyle={{
                    width: "100%",
                    height: "30px",
                    borderRadius: "8px",
                    border: (touchedFields.phone && fieldErrors.phone)
                      ? "1px solid #d32f2f"
                      : "1px solid #ccc",
                    fontSize: "14px",
                  }}
                  containerStyle={{
                    width: "100%",
                    marginBottom: "0px",
                  }}
                  buttonStyle={{
                    height: "30px",
                    borderRadius: "8px 0 0 8px",
                  }}
                  dropdownStyle={{
                    maxHeight: "200px",
                  }}
                />
                {touchedFields.phone && fieldErrors.phone && (
                  <Typography
                    variant="caption"
                    color="error"
                    display="block"
                    sx={{ mt: -0.5, mb: 1, fontSize: "0.75rem" }}
                  >
                    {fieldErrors.phone}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <TextField
                  select
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={inputValues[item.name]}
                  onChange={(e) => handleInputChange(item.name, e.target.value)}
                  onBlur={() => handleBlur(item.name)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      height: "30px",
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      sx: {
                        "& .MuiPaper-root": {
                          backgroundColor: "white",
                        },
                      },
                    },
                  }}
                >
                  {item.options?.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
          </Grid>
        );
      })}
 
      {error && (
        <Typography
          variant="caption"
          color="error"
          textAlign="center"
          display="block"
          sx={{ mt: 0.5, mb: 0.5, fontSize: "0.7rem" }}
        >
          {error}
        </Typography>
      )}

      <Button
        variant="contained"
        fullWidth
        size="small"
        disabled={!isFormValid || isSubmitting}
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          mt: 0.5,
          padding: "8px",
          borderRadius: "10px",
          "&:hover": { backgroundColor: "#333" },
          "&:disabled": {
            backgroundColor: "#ccc",
            color: "#666",
          },
        }}
        onClick={handleSubmit}
      >
        {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
      </Button>
      </Grid>
    </Grid>
  );
};
 
export default CreditsRequestsForm;
